// src/pages/FacturacionPresupuestos/DocumentSectionsEditor.js
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { 
    FaBuilding, 
    FaUserCheck, 
    FaFileContract, 
    FaListOl, 
    FaCalculator, 
    FaUniversity, 
    FaSignature, 
    FaPlus, 
    FaTrash, 
    FaCopy,
    FaEye, 
    FaSave, 
    FaPrint, 
    FaWhatsapp, 
    FaTags,
    FaCommentDots,
    FaArrowsAltV,
    FaChevronDown,
    FaChevronUp,
    FaCheckSquare,
    FaSquare,
    FaBolt,
    FaCreditCard,
    FaUpload,
    FaCheckCircle,
    FaBold,
    FaItalic,
    FaUnderline,
    FaAlignLeft,
    FaAlignCenter,
    FaAlignRight,
    FaAlignJustify,
    FaFont
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { numeroALetras } from '../../utils/numeroALetras';
import { NOTAS_CLAVE_PRESETS } from './catalogoInicialDatos';
import { ESTRUCTURAS_CATEGORIAS, PLANTILLAS_BASE, UNIDADES_LIST } from '../../utils/plantillasDocumentos';
import { generarFolioInteligente, actualizarSiglasEnFolio } from '../../utils/folioFacturacionUtils';
import { 
    formatearTextoEnriquecidoHtml, 
    tieneFormatoEnriquecido,
    detectarFormatosActivos,
    alternarFormatoEnTexto,
    sanearMarcasFormato
} from '../../utils/formatoTextoUtils';
import {
    formatearMoneda,
    generarTextoInversionInicial,
    sincronizarTextoInversionInicial
} from '../../utils/inversionInicialUtils';
import SectionFontSizeControl from './SectionFontSizeControl';
import './DocumentSectionsEditor.css';

const DocumentSectionsEditor = ({
    documento,
    setDocumento,
    onGuardar,
    onVerVistaPrevia,
    onAbrirSendGrid,
    onCompartirWhatsApp,
    onImprimir,
    onNuevoDocumento,
    onSembrarCatalogo,
    sembrandoCatalogo = false,
    guardando = false,
    configuracion = {},
    modelosOficiales = {}
}) => {
    // Categoría oficial del documento actual (presupuesto, remision, pago)
    const tipoActual = (documento?.tipoDocumento || 'presupuesto').toLowerCase();

    // Dropdown para nuevo documento
    const [dropdownNuevoAbierto, setDropdownNuevoAbierto] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownNuevoAbierto(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Estados para navegación inteligente de partidas (Punto 3)
    const [partidaActivaIndex, setPartidaActivaIndex] = useState(0);
    const [modoVistaPartidas, setModoVistaPartidas] = useState('activa'); // 'activa' o 'todas'
    const [expandedDescripciones, setExpandedDescripciones] = useState({});
    const [expandedNotaClave, setExpandedNotaClave] = useState(false);

    // Estado para expandir/colapsar herramientas de tipografía (Punto 5)
    const [mostrarToolbarTipografia, setMostrarToolbarTipografia] = useState(false);

    // Estado de formatos activos en la selección actual para indicadores de la barra
    const [formatosActivos, setFormatosActivos] = useState({ bold: false, italic: false, underline: false });

    useEffect(() => {
        setFormatosActivos({ bold: false, italic: false, underline: false });
    }, [partidaActivaIndex]);

    // Referencias para memoria de selección de texto en cursor
    const ultimoElementoEnfocadoRef = useRef(null);
    const descripcionInputRef = useRef(null);
    const subtituloTextareaRef = useRef(null);

    // Registrar selección activa de texto con cursor y actualizar estado de botones en tiempo real
    const registrarSeleccion = (campo, e) => {
        if (!e || !e.target) return;
        const el = e.target;
        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;
        ultimoElementoEnfocadoRef.current = {
            campo,
            elemento: el,
            start,
            end
        };
        const activos = detectarFormatosActivos(el.value || '', start, end);
        setFormatosActivos(activos);
    };

    // Estado para la barra superior colapsable de opciones por secciones (Punto 7)
    const [panelOpcionesExpandido, setPanelOpcionesExpandido] = useState(false);
    const panelOpcionesRef = useRef(null);

    useEffect(() => {
        const handleClickFueraOpciones = (e) => {
            if (panelOpcionesRef.current && !panelOpcionesRef.current.contains(e.target)) {
                setPanelOpcionesExpandido(false);
            }
        };
        if (panelOpcionesExpandido) {
            document.addEventListener('mousedown', handleClickFueraOpciones);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickFueraOpciones);
        };
    }, [panelOpcionesExpandido]);

    // Saneamiento proactivo de partidas: erradica viñetas o hardwareSpecs residuales de plantillas antiguas
    useEffect(() => {
        if (Array.isArray(documento?.conceptos)) {
            const tieneSpecsResiduales = documento.conceptos.some(
                c => Array.isArray(c.hardwareSpecs) && c.hardwareSpecs.length > 0
            );
            if (tieneSpecsResiduales) {
                setDocumento(prev => ({
                    ...prev,
                    conceptos: (prev.conceptos || []).map(c => {
                        if (Array.isArray(c.hardwareSpecs) && c.hardwareSpecs.length > 0) {
                            const { hardwareSpecs, ...limpio } = c;
                            return { ...limpio, hardwareSpecs: [] };
                        }
                        return c;
                    })
                }));
            }

            // Saneamiento de marcas tipográficas corruptas o duplicadas (ej. ****, <u><u>)
            const tieneMarcasCorruptas = documento.conceptos.some(
                c => /(\*{3,}|(<u>\s*){2,}|(<\/u>\s*){2,})/.test(c.subtitulo || '') ||
                     /(\*{3,}|(<u>\s*){2,}|(<\/u>\s*){2,})/.test(c.descripcion || '')
            );
            if (tieneMarcasCorruptas) {
                setDocumento(prev => ({
                    ...prev,
                    conceptos: (prev.conceptos || []).map(c => ({
                        ...c,
                        descripcion: sanearMarcasFormato(c.descripcion || ''),
                        subtitulo: sanearMarcasFormato(c.subtitulo || '')
                    }))
                }));
            }
        }
    }, [documento?.conceptos, setDocumento]);

    // Cálculos financieros
    const { subtotal, totalDescuento, montoIva, totalFinal } = useMemo(() => {
        const sub = (documento.conceptos || []).reduce((acc, curr) => {
            const cant = Number(curr.cantidad) || 0;
            const precio = Number(curr.precioUnitario) || 0;
            return acc + (cant * precio);
        }, 0);

        const desc = Number(documento.descuento) || 0;
        const baseIva = Math.max(0, sub - desc);
        const iva = documento.aplicaIva ? (baseIva * ((Number(documento.tasaIva) || 16) / 100)) : 0;
        const total = baseIva + iva;

        return {
            subtotal: sub,
            totalDescuento: desc,
            montoIva: iva,
            totalFinal: total
        };
    }, [documento.conceptos, documento.descuento, documento.aplicaIva, documento.tasaIva]);

    const importeEnLetras = useMemo(() => {
        return numeroALetras(totalFinal, 'PESOS', 'M.N.');
    }, [totalFinal]);

    // Sincronización inteligente y automática de Inversión Inicial con el costo total (con o sin IVA)
    useEffect(() => {
        if (tipoActual === 'presupuesto' && documento?.condiciones?.inversionInicial) {
            const textoActual = documento.condiciones.inversionInicial;
            const textoSincronizado = sincronizarTextoInversionInicial(textoActual, totalFinal);
            if (textoSincronizado && textoSincronizado !== textoActual) {
                setDocumento(prev => {
                    if (!prev || !prev.condiciones) return prev;
                    if (prev.condiciones.inversionInicial === textoSincronizado) return prev;
                    return {
                        ...prev,
                        condiciones: {
                            ...prev.condiciones,
                            inversionInicial: textoSincronizado
                        }
                    };
                });
            }
        }
    }, [totalFinal, tipoActual, documento?.condiciones?.inversionInicial, setDocumento]);

    // Actualización de campos anidados con clonación profunda inmutable para reactividad en todas las secciones (Punto 1)
    const handleUpdate = (path, valor) => {
        setDocumento(prev => {
            const copia = JSON.parse(JSON.stringify(prev || {}));
            const keys = path.split('.');
            let actual = copia;
            for (let i = 0; i < keys.length - 1; i++) {
                if (!actual[keys[i]]) actual[keys[i]] = {};
                actual = actual[keys[i]];
            }
            actual[keys[keys.length - 1]] = valor;

            // Sincronización automática de firmas
            if (path === 'cliente.contacto') {
                if (!copia.firmas) copia.firmas = {};
                copia.firmas.receptorNombre = valor || 'Representante Acreditado';
            }
            if (path === 'cliente.nombre') {
                if (!copia.firmas) copia.firmas = {};
                copia.firmas.receptorCargo = `Aceptación y Visto Bueno de ${valor || 'el Condominio'}`;
            }

            // Sincronización automática de detalles de emisor si se editan campos de emisor
            if (path.startsWith('emisor.')) {
                if (!copia.emisor) copia.emisor = {};
                const partes = [
                    copia.emisor.direccion,
                    copia.emisor.contactoInfo,
                    copia.emisor.rfc ? `RFC: ${copia.emisor.rfc}` : null
                ].filter(Boolean);
                if (partes.length > 0) {
                    copia.emisor.detalles = partes.join(' · ');
                }
            }

            return copia;
        });
    };

    // Actualización de partida individual
    const handleUpdateConcepto = (index, campo, valor) => {
        setDocumento(prev => {
            const nuevos = [...(prev.conceptos || [])];
            const partidaActual = { ...nuevos[index] };
            if (partidaActual.hardwareSpecs) {
                delete partidaActual.hardwareSpecs;
            }
            partidaActual[campo] = valor;
            nuevos[index] = partidaActual;
            return { ...prev, conceptos: nuevos };
        });
    };

    // Alternar estilo tipográfico y alineación para la partida activa y lienzo (Punto 5)
    const handleToggleTipografiaPartida = (propiedad, valorActivo, valorInactivo = '') => {
        const partida = (documento.conceptos || [])[partidaValidaIndex] || {};
        const estilosActuales = partida.estilos || documento.estilos || {};
        const valorActual = estilosActuales[propiedad] || '';
        const nuevoValor = valorActual === valorActivo ? valorInactivo : valorActivo;

        const nuevosEstilos = {
            ...estilosActuales,
            [propiedad]: nuevoValor
        };

        handleUpdateConcepto(partidaValidaIndex, 'estilos', nuevosEstilos);

        setDocumento(prev => ({
            ...prev,
            estilos: {
                ...(prev.estilos || {}),
                [propiedad]: nuevoValor
            }
        }));
    };

    // Aplicar formato exclusivo al texto seleccionado con el cursor con bloqueo de duplicación y toggle
    const aplicarFormatoSeleccion = (tipo) => {
        const targetInfo = ultimoElementoEnfocadoRef.current;
        const campo = targetInfo?.campo || 'subtitulo';
        const elemento = targetInfo?.elemento || (campo === 'descripcion' ? descripcionInputRef.current : subtituloTextareaRef.current);

        const partida = (documento.conceptos || [])[partidaValidaIndex] || {};
        const textoOriginal = partida[campo] || '';

        let start = (elemento && typeof elemento.selectionStart === 'number') ? elemento.selectionStart : (targetInfo?.start ?? textoOriginal.length);
        let end = (elemento && typeof elemento.selectionEnd === 'number') ? elemento.selectionEnd : (targetInfo?.end ?? textoOriginal.length);

        const resultado = alternarFormatoEnTexto(textoOriginal, start, end, tipo);

        handleUpdateConcepto(partidaValidaIndex, campo, resultado.nuevoTexto);

        // Actualizar indicadores activos de tipografía de inmediato
        const nuevosFormatos = detectarFormatosActivos(resultado.nuevoTexto, resultado.nuevoStart, resultado.nuevoEnd);
        setFormatosActivos(nuevosFormatos);

        // Restablecer foco y selección
        setTimeout(() => {
            if (elemento && typeof elemento.focus === 'function') {
                elemento.focus();
                if (typeof elemento.setSelectionRange === 'function') {
                    try {
                        elemento.setSelectionRange(resultado.nuevoStart, resultado.nuevoEnd);
                        ultimoElementoEnfocadoRef.current = {
                            campo,
                            elemento,
                            start: resultado.nuevoStart,
                            end: resultado.nuevoEnd
                        };
                    } catch (e) {
                        // Ignorar en entornos sin setSelectionRange
                    }
                }
            }
        }, 15);
    };

    // Agregar nueva partida
    const handleAgregarPartida = () => {
        const nuevaPartida = {
            id: Date.now().toString(),
            cantidad: 1,
            unidad: 'Servicio',
            descripcion: '',
            subtitulo: '',
            periodo: 'Único',
            precioUnitario: 0
        };
        const nuevos = [...(documento.conceptos || []), nuevaPartida];
        setDocumento(prev => ({
            ...prev,
            conceptos: nuevos
        }));
        setPartidaActivaIndex(nuevos.length - 1);
    };

    // Duplicar partida
    const handleDuplicarPartida = (index) => {
        const partidaOriginal = (documento.conceptos || [])[index];
        if (!partidaOriginal) return;
        const clon = JSON.parse(JSON.stringify(partidaOriginal));
        clon.id = Date.now().toString();
        clon.descripcion = `${clon.descripcion || ''} (Copia)`;
        delete clon.hardwareSpecs;
        const nuevos = [...(documento.conceptos || [])];
        nuevos.splice(index + 1, 0, clon);
        setDocumento(prev => ({ ...prev, conceptos: nuevos }));
        setPartidaActivaIndex(index + 1);
    };

    // Eliminar partida
    const handleEliminarPartida = (index) => {
        const nuevos = (documento.conceptos || []).filter((_, i) => i !== index);
        setDocumento(prev => ({
            ...prev,
            conceptos: nuevos
        }));
        if (partidaActivaIndex >= nuevos.length) {
            setPartidaActivaIndex(Math.max(0, nuevos.length - 1));
        }
    };

    // Alternar expansión de textarea individual
    const toggleExpandDescripcion = (idx) => {
        setExpandedDescripciones(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    // Lista de cuentas bancarias configuradas disponibles para mandar llamar (Punto 2)
    const listaBancosConfigurados = useMemo(() => {
        if (Array.isArray(configuracion.cuentasBancarias) && configuracion.cuentasBancarias.length > 0) {
            return configuracion.cuentasBancarias;
        }
        if (configuracion.banco || configuracion.clabe) {
            return [{
                id: 'default_1',
                banco: configuracion.banco || 'BBVA Bancomer',
                beneficiario: configuracion.beneficiario || 'Domus Habita',
                clabe: configuracion.clabe || '',
                cuenta: configuracion.cuenta || '',
                tarjeta: configuracion.tarjeta || ''
            }];
        }
        return [];
    }, [configuracion]);

    // Estructura inteligente según categoría oficial activa
    const estructuraActiva = ESTRUCTURAS_CATEGORIAS[tipoActual] || ESTRUCTURAS_CATEGORIAS.presupuesto;

    // Cambio inteligente de categoría con confirmación Zen
    const handleCambiarCategoria = async (nuevoTipo) => {
        if (!nuevoTipo || nuevoTipo === tipoActual) return;
        
        const estructNueva = ESTRUCTURAS_CATEGORIAS[nuevoTipo] || ESTRUCTURAS_CATEGORIAS.presupuesto;
        const plantillaOficial = (modelosOficiales && modelosOficiales[nuevoTipo]) || PLANTILLAS_BASE[nuevoTipo] || PLANTILLAS_BASE.presupuesto;

        const res = await Swal.fire({
            title: `¿Cambiar a ${estructNueva.nombreLabel}?`,
            html: `<div style="text-align: left; font-size: 13.5px; color: #334155; line-height: 1.5;">
                Has seleccionado la categoría: <strong style="color: #1e3a8a;">${estructNueva.nombreLabel}</strong>.<br><br>
                ¿Deseas <strong>cargar la plantilla modelo oficial de Firebase Cloud</strong> (con partidas, condiciones y notas de referencia reales) o prefieres <strong>conservar tus datos actuales</strong> adaptando los títulos y la estructura?
            </div>`,
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonColor: '#1e3a8a',
            denyButtonColor: '#059669',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: '🪄 Cargar Plantilla Modelo Oficial',
            denyButtonText: '📝 Conservar Mis Datos Actuales',
            cancelButtonText: 'Cancelar'
        });

        if (res.isConfirmed) {
            // Cargar plantilla modelo oficial completa conservando firma del emisor, folio inteligente y espacio en blanco del cliente
            setDocumento(prev => {
                const firmaEmisor = prev?.firmas?.emisorFirmaUrl || prev?.firmaUrl || configuracion?.firmas?.[0]?.url || '';
                const emisorNombre = prev?.firmas?.emisorNombre || configuracion?.firmanteNombre || prev?.emisor?.nombre || plantillaOficial.firmas?.emisorNombre;
                const firmaClienteActiva = prev?.incluirFirmaCliente !== undefined ? prev.incluirFirmaCliente : true;

                // Generar automáticamente el folio inteligente de la nueva serie oficial
                const clienteActivo = prev?.cliente?.nombre ? prev.cliente : plantillaOficial.cliente;
                const fechaEmisionActiva = prev?.fechaEmision || new Date().toISOString().split('T')[0];
                const nuevoFolio = generarFolioInteligente({
                    tipo: nuevoTipo,
                    configuracion,
                    cliente: clienteActivo,
                    fechaEmision: fechaEmisionActiva
                });

                return {
                    ...JSON.parse(JSON.stringify(plantillaOficial)),
                    id: prev?.id || Date.now().toString(),
                    folio: nuevoFolio,
                    incluirFirmaCliente: firmaClienteActiva,
                    firmaUrl: firmaEmisor,
                    emisor: {
                        ...plantillaOficial.emisor,
                        ...(prev?.emisor?.nombre ? { nombre: prev.emisor.nombre } : {}),
                        ...(prev?.emisor?.logo1Url ? { logo1Url: prev.emisor.logo1Url } : {})
                    },
                    firmas: {
                        ...plantillaOficial.firmas,
                        emisorNombre: emisorNombre || 'C. Andrés Jonnatan Wong Feregrino',
                        emisorCargo: estructNueva.rolEmisorSugerido || plantillaOficial.firmas?.emisorCargo,
                        emisorFirmaUrl: firmaEmisor,
                        receptorNombre: prev?.firmas?.receptorNombre || prev?.cliente?.contacto || plantillaOficial.firmas?.receptorNombre || '',
                        receptorCargo: estructNueva.rolReceptorSugerido || plantillaOficial.firmas?.receptorCargo,
                        receptorFirmaUrl: '' // Siempre en blanco garantizado para la firma autógrafa del cliente
                    }
                };
            });
            Swal.fire({
                title: '¡Estructura Modelo Cargada!',
                text: `Se cargó el formato oficial para ${estructNueva.nombreLabel} con folio actualizado a la serie correspondiente.`,
                icon: 'success',
                timer: 1600,
                showConfirmButton: false
            });
        } else if (res.isDenied) {
            // Adaptar títulos, badges y roles actualizando el folio a la nueva categoría
            setDocumento(prev => {
                const copia = JSON.parse(JSON.stringify(prev || {}));
                copia.tipoDocumento = nuevoTipo;
                copia.tituloDocumento = estructNueva.tituloDocDefault;
                copia.badgeEstado = estructNueva.badgeDefault;

                // Actualizar automáticamente el folio a la serie de la nueva categoría
                copia.folio = generarFolioInteligente({
                    tipo: nuevoTipo,
                    configuracion,
                    cliente: copia.cliente,
                    fechaEmision: copia.fechaEmision
                });

                if (nuevoTipo === 'pago') {
                    copia.fechaPago = copia.fechaPago || copia.fechaEmision || new Date().toISOString().split('T')[0];
                    copia.fechaVigencia = '';
                    copia.fechaVencimiento = '';
                } else if (nuevoTipo === 'remision') {
                    copia.fechaVencimiento = copia.fechaVencimiento || copia.fechaVigencia || '5 días hábiles a partir de su emisión';
                    copia.fechaVigencia = '';
                } else {
                    copia.fechaVigencia = copia.fechaVigencia || copia.fechaVencimiento || '15 días naturales';
                    copia.fechaVencimiento = '';
                }
                if (!copia.firmas) copia.firmas = {};
                copia.firmas.emisorCargo = estructNueva.rolEmisorSugerido;
                copia.firmas.receptorCargo = estructNueva.rolReceptorSugerido;
                copia.firmas.emisorFirmaUrl = copia.firmas.emisorFirmaUrl || copia.firmaUrl || configuracion?.firmas?.[0]?.url || '';
                copia.firmas.receptorFirmaUrl = ''; // Espacio en blanco garantizado para el cliente
                copia.firmaUrl = copia.firmas.emisorFirmaUrl;
                return copia;
            });
            Swal.fire({
                title: 'Estructura Adaptada',
                text: `Títulos y serie de folio actualizados a ${estructNueva.nombreLabel} conservando tu contenido y firma.`,
                icon: 'info',
                timer: 1500,
                showConfirmButton: false
            });
        }
    };

    // Cargar directamente los datos modelo de la categoría actual
    const handleCargarModeloActual = async () => {
        const estruct = ESTRUCTURAS_CATEGORIAS[tipoActual] || ESTRUCTURAS_CATEGORIAS.presupuesto;
        const plantillaOficial = (modelosOficiales && modelosOficiales[tipoActual]) || PLANTILLAS_BASE[tipoActual] || PLANTILLAS_BASE.presupuesto;
        
        const res = await Swal.fire({
            title: `¿Cargar Modelo Oficial de ${estruct.nombreLabel}?`,
            text: `Se aplicarán las partidas, condiciones y notas de referencia oficiales de ${estruct.nombreLabel} preservando tu firma y folio.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1e3a8a',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: 'Sí, aplicar modelo oficial',
            cancelButtonText: 'Cancelar'
        });
        if (!res.isConfirmed) return;
        
        setDocumento(prev => {
            const firmaEmisor = prev?.firmas?.emisorFirmaUrl || prev?.firmaUrl || configuracion?.firmas?.[0]?.url || '';
            const emisorNombre = prev?.firmas?.emisorNombre || configuracion?.firmanteNombre || prev?.emisor?.nombre || plantillaOficial.firmas?.emisorNombre;
            const firmaClienteActiva = prev?.incluirFirmaCliente !== undefined ? prev.incluirFirmaCliente : true;

            const clienteActivo = prev?.cliente?.nombre ? prev.cliente : plantillaOficial.cliente;
            const nuevoFolio = generarFolioInteligente({
                tipo: tipoActual,
                configuracion,
                cliente: clienteActivo,
                fechaEmision: prev?.fechaEmision || new Date().toISOString().split('T')[0]
            });

            return {
                ...JSON.parse(JSON.stringify(plantillaOficial)),
                id: prev?.id || Date.now().toString(),
                folio: nuevoFolio,
                incluirFirmaCliente: firmaClienteActiva,
                firmaUrl: firmaEmisor,
                emisor: {
                    ...plantillaOficial.emisor,
                    ...(prev?.emisor?.nombre ? { nombre: prev.emisor.nombre } : {}),
                    ...(prev?.emisor?.logo1Url ? { logo1Url: prev.emisor.logo1Url } : {})
                },
                firmas: {
                    ...plantillaOficial.firmas,
                    emisorNombre: emisorNombre || 'C. Andrés Jonnatan Wong Feregrino',
                    emisorCargo: estruct.rolEmisorSugerido || plantillaOficial.firmas?.emisorCargo,
                    emisorFirmaUrl: firmaEmisor,
                    receptorNombre: prev?.firmas?.receptorNombre || prev?.cliente?.contacto || plantillaOficial.firmas?.receptorNombre || '',
                    receptorCargo: estruct.rolReceptorSugerido || plantillaOficial.firmas?.receptorCargo,
                    receptorFirmaUrl: '' // Espacio en blanco garantizado para la firma del cliente
                }
            };
        });

        Swal.fire({
            title: 'Modelo Aplicado',
            text: `Se han cargado los datos oficiales de ${estruct.nombreLabel} conservando tu firma activa.`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleCargarBancoConfigurado = (bancoId, modo = 'reemplazar_primera') => {
        if (!bancoId) return;
        const cuenta = listaBancosConfigurados.find(b => b.id === bancoId);
        if (cuenta) {
            setDocumento(prev => {
                const copiaCuentas = Array.isArray(prev.cuentasBancarias) ? [...prev.cuentasBancarias] : [];
                const nuevaCta = {
                    id: Date.now().toString(),
                    banco: cuenta.banco || '',
                    titular: cuenta.beneficiario || cuenta.titular || prev.emisor?.nombre || 'Domus Habita',
                    clabe: cuenta.clabe || '',
                    cuenta: cuenta.cuenta || '',
                    tarjeta: cuenta.tarjeta || ''
                };

                if (modo === 'agregar_nueva') {
                    return {
                        ...prev,
                        cuentasBancarias: [...copiaCuentas, nuevaCta]
                    };
                } else {
                    if (copiaCuentas.length === 0) {
                        copiaCuentas.push(nuevaCta);
                    } else {
                        copiaCuentas[0] = { ...copiaCuentas[0], ...nuevaCta };
                    }
                    return {
                        ...prev,
                        cuentasBancarias: copiaCuentas,
                        datosBancarios: { ...nuevaCta }
                    };
                }
            });
        }
    };

    const handleAgregarCuentaBancaria = () => {
        setDocumento(prev => {
            const cuentasActuales = Array.isArray(prev.cuentasBancarias) ? [...prev.cuentasBancarias] : [];
            const nuevaCta = {
                id: Date.now().toString(),
                banco: '',
                titular: prev.emisor?.nombre || 'Domus Habita Soluciones Residenciales',
                clabe: '',
                cuenta: '',
                tarjeta: ''
            };
            return {
                ...prev,
                cuentasBancarias: [...cuentasActuales, nuevaCta]
            };
        });
    };

    const handleEliminarCuentaBancaria = (idx) => {
        setDocumento(prev => {
            const cuentasActuales = Array.isArray(prev.cuentasBancarias) ? [...prev.cuentasBancarias] : [];
            const filtradas = cuentasActuales.filter((_, i) => i !== idx);
            return {
                ...prev,
                cuentasBancarias: filtradas.length > 0 ? filtradas : [{
                    id: Date.now().toString(),
                    banco: '',
                    titular: prev.emisor?.nombre || 'Domus Habita',
                    clabe: '',
                    cuenta: '',
                    tarjeta: ''
                }]
            };
        });
    };

    // Subir rúbrica o firma digital del emisor directamente desde la Sección 8
    const handleSubirFirmaDirecta = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const base64 = event.target.result;
            handleUpdate('firmas.emisorFirmaUrl', base64);
            handleUpdate('firmaUrl', base64);
        };
        reader.readAsDataURL(file);
    };

    // Subir logotipo directamente desde la Sección 1
    const handleSubirLogoDirecto = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const base64 = event.target.result;
            handleUpdate('emisor.logo1Url', base64);
            handleUpdate('emisor.logoUrl', base64);
            handleUpdate('emisor.mostrarLogo1', true);
        };
        reader.readAsDataURL(file);
    };

    // Regenerar folio inteligente según cliente, categoría y año
    const handleRegenerarFolioInteligente = () => {
        const folioNuevo = generarFolioInteligente({
            tipo: tipoActual,
            configuracion,
            cliente: documento.cliente,
            fechaEmision: documento.fechaEmision
        });
        handleUpdate('folio', folioNuevo);
        Swal.fire({
            title: 'Folio Actualizado',
            text: `Nuevo folio oficial generado: ${folioNuevo}`,
            icon: 'info',
            timer: 1400,
            showConfirmButton: false
        });
    };

    // Plantillas de nota clave: predeterminadas + personalizadas (Punto 4)
    const [plantillasNotaClave, setPlantillasNotaClave] = useState(() => {
        try {
            const guardadas = localStorage.getItem('plantillas_nota_clave_custom');
            if (guardadas) {
                const parsed = JSON.parse(guardadas);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    return [...NOTAS_CLAVE_PRESETS, ...parsed];
                }
            }
        } catch (e) {
            console.warn('Error al leer plantillas guardadas:', e);
        }
        return NOTAS_CLAVE_PRESETS;
    });
    const [plantillaSeleccionadaId, setPlantillaSeleccionadaId] = useState('');

    const handleSeleccionarPlantilla = (id) => {
        setPlantillaSeleccionadaId(id);
        if (!id) return;
        const encontrada = plantillasNotaClave.find(p => p.id === id);
        if (encontrada) {
            setDocumento(prev => ({
                ...prev,
                tituloNotaClave: encontrada.titulo,
                impactoComunitario: encontrada.texto,
                mostrarNotaClave: true
            }));
        }
    };

    const handleGuardarPlantillaNotaClave = () => {
        const titulo = documento.tituloNotaClave?.trim() || 'Plantilla Personalizada';
        const texto = documento.impactoComunitario?.trim();
        if (!texto) {
            alert('Por favor escribe o redacta primero el texto de la nota clave antes de guardarla como plantilla.');
            return;
        }
        const nuevaPlantilla = {
            id: `custom_${Date.now()}`,
            titulo: titulo,
            texto: texto,
            esCustom: true
        };
        const actualizadas = [...plantillasNotaClave, nuevaPlantilla];
        setPlantillasNotaClave(actualizadas);
        setPlantillaSeleccionadaId(nuevaPlantilla.id);
        try {
            const soloCustom = actualizadas.filter(p => p.esCustom);
            localStorage.setItem('plantillas_nota_clave_custom', JSON.stringify(soloCustom));
        } catch (e) {
            console.warn('Error al guardar plantilla en localStorage:', e);
        }
        alert(`¡Plantilla "${titulo}" guardada exitosamente en tus opciones rápidas!`);
    };

    const handleEliminarPlantillaNotaClave = async () => {
        if (!plantillaSeleccionadaId) return;
        const plantilla = plantillasNotaClave.find(p => p.id === plantillaSeleccionadaId);
        if (!plantilla) return;

        const resConfirm = await Swal.fire({
            title: '¿Eliminar Plantilla?',
            text: `¿Deseas eliminar permanentemente la plantilla "${plantilla.titulo}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!resConfirm.isConfirmed) return;

        const actualizadas = plantillasNotaClave.filter(p => p.id !== plantillaSeleccionadaId);
        setPlantillasNotaClave(actualizadas);
        setPlantillaSeleccionadaId('');
        try {
            const soloCustom = actualizadas.filter(p => p.esCustom);
            localStorage.setItem('plantillas_nota_clave_custom', JSON.stringify(soloCustom));
        } catch (e) {
            console.warn('Error al actualizar plantillas en localStorage:', e);
        }

        Swal.fire({
            icon: 'success',
            title: 'Plantilla Eliminada',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
        });
    };

    const conceptosTotal = documento.conceptos?.length || 0;
    const partidaValidaIndex = Math.min(Math.max(0, partidaActivaIndex), Math.max(0, conceptosTotal - 1));

    // Partida a editar en modo 1 a la vez
    const partidaActiva = (documento.conceptos || [])[partidaValidaIndex];
    const estilosPartidaActiva = partidaActiva?.estilos || documento.estilos || {};

    return (
        <div className="sections-editor-container">
            {/* Tarjeta de Control Superior Colapsable: Opciones de Edición por Secciones (Puntos 6, 7 y 8) */}
            <div className="editor-top-actions-card colapsable-zen" ref={panelOpcionesRef} style={{ marginBottom: '14px', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 6px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
                <div 
                    className="editor-top-actions-header" 
                    onClick={() => setPanelOpcionesExpandido(prev => !prev)}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '10px 16px',
                        background: panelOpcionesExpandido ? '#f8fafc' : '#ffffff',
                        transition: 'background 0.2s ease',
                        userSelect: 'none'
                    }}
                    title="Haz clic para mostrar u ocultar las opciones rápidas de sección"
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="editor-mode-badge" style={{ background: '#f1f5f9', color: '#1e293b', fontWeight: '700', padding: '4px 10px', borderRadius: '6px', fontSize: '12px' }}>
                            📝 Modo Editor por Secciones
                        </span>
                        <span className="editor-mode-hint" style={{ fontSize: '11.5px', color: '#64748b' }}>
                            (Opciones del documento y tamaños de texto)
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '11.5px', color: panelOpcionesExpandido ? '#16a34a' : '#64748b', fontWeight: '600' }}>
                            {panelOpcionesExpandido ? 'Ocultar Opciones' : 'Desplegar Opciones'}
                        </span>
                        <button
                            type="button"
                            style={{
                                background: panelOpcionesExpandido ? '#dcfce7' : '#f1f5f9',
                                color: panelOpcionesExpandido ? '#16a34a' : '#475569',
                                border: 'none',
                                borderRadius: '50%',
                                width: '26px',
                                height: '26px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '11px'
                            }}
                        >
                            {panelOpcionesExpandido ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                    </div>
                </div>

                {panelOpcionesExpandido && (
                    <div className="editor-top-actions-content" style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                {/* Botón: Cargar Modelo de la Categoría Activa */}
                                <button
                                    type="button"
                                    onClick={handleCargarModeloActual}
                                    style={{
                                        background: '#f0fdf4',
                                        color: '#16a34a',
                                        border: '1px solid #bbf7d0',
                                        padding: '5px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        cursor: 'pointer'
                                    }}
                                    title={`Pre-cargar datos oficiales de referencia para ${estructuraActiva.nombreLabel}`}
                                >
                                    <FaBolt /> Cargar Modelo {estructuraActiva.nombreLabel}
                                </button>

                                {/* Selector rápido para incluir firma del cliente */}
                                <label className="firma-fast-toggle" style={{ margin: 0 }}>
                                    <input 
                                        type="checkbox"
                                        checked={documento.incluirFirmaCliente !== false}
                                        onChange={(e) => handleUpdate('incluirFirmaCliente', e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <span>Firma de Aceptación Cliente</span>
                                </label>
                            </div>

                            {/* Botón: Menú Desplegable Nuevo Documento */}
                            {onNuevoDocumento && (
                                <div className="dropdown-nuevo-doc" ref={dropdownRef}>
                                    <button 
                                        type="button" 
                                        className="btn-dropdown-nuevo-doc"
                                        onClick={() => setDropdownNuevoAbierto(prev => !prev)}
                                        title="Crear un nuevo documento comercial"
                                    >
                                        <FaPlus /> Nuevo Documento <FaChevronDown style={{ fontSize: '10px' }} />
                                    </button>
                                    {dropdownNuevoAbierto && (
                                        <div className="dropdown-menu-nuevo-doc">
                                            <button 
                                                type="button" 
                                                className="dropdown-item-doc"
                                                onClick={() => {
                                                    onNuevoDocumento('presupuesto');
                                                    setDropdownNuevoAbierto(false);
                                                }}
                                            >
                                                📑 Presupuesto Formal
                                            </button>
                                            <button 
                                                type="button" 
                                                className="dropdown-item-doc"
                                                onClick={() => {
                                                    onNuevoDocumento('remision');
                                                    setDropdownNuevoAbierto(false);
                                                }}
                                            >
                                                📋 Remisión de Cobro
                                            </button>
                                            <button 
                                                type="button" 
                                                className="dropdown-item-doc"
                                                onClick={() => {
                                                    onNuevoDocumento('pago');
                                                    setDropdownNuevoAbierto(false);
                                                }}
                                            >
                                                🧾 Nota de Pago
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Barra de Ajuste de Tamaño de Texto por Sección */}
                        <div style={{ width: '100%' }}>
                            <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="bar" />
                        </div>
                    </div>
                )}
            </div>

            {/* SECCIÓN 1: DATOS DE LA EMPRESA & FOLIO */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaBuilding style={{ color: '#16a34a' }} /> 1. Empresa Emisora y Encabezado del Documento
                    </h3>
                    <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="emisor" />
                </div>
                <div className="editor-section-body">
                    {/* Control de Logotipo de la Empresa Emisora */}
                    <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ height: '55px', width: '130px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', borderRadius: '6px', border: '1px dashed #cbd5e1', padding: '4px' }}>
                                {(documento.emisor?.logo1Url || documento.emisor?.logoUrl) ? (
                                    <img 
                                        src={documento.emisor.logo1Url || documento.emisor.logoUrl} 
                                        alt="Logo Emisor" 
                                        style={{ maxHeight: '47px', maxWidth: '120px', objectFit: 'contain' }} 
                                    />
                                ) : (
                                    <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Sin Logotipo</span>
                                )}
                            </div>
                            <div>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#0f172a' }}>Logotipo del Encabezado</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{ cursor: 'pointer', fontSize: '12px', color: '#1e3a8a', fontWeight: '600', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <FaUpload style={{ fontSize: '11px' }} /> Cambiar / Subir Logo
                                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSubirLogoDirecto} />
                                    </label>
                                    {(documento.emisor?.logo1Url || documento.emisor?.logoUrl) && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleUpdate('emisor.logo1Url', '');
                                                handleUpdate('emisor.logoUrl', '');
                                            }}
                                            style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '11.5px', cursor: 'pointer', padding: 0 }}
                                        >
                                            Quitar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Selector de logos guardados en la configuración */}
                        {Array.isArray(configuracion.logos) && configuracion.logos.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '500' }}>Logos guardados:</span>
                                {configuracion.logos.map((l, i) => {
                                    const activo = (documento.emisor?.logo1Url === l.url || documento.emisor?.logoUrl === l.url);
                                    return (
                                        <button
                                            key={l.id || i}
                                            type="button"
                                            onClick={() => {
                                                handleUpdate('emisor.logo1Url', l.url);
                                                handleUpdate('emisor.logoUrl', l.url);
                                                handleUpdate('emisor.mostrarLogo1', true);
                                            }}
                                            style={{
                                                height: '38px',
                                                width: '54px',
                                                padding: '2px',
                                                border: activo ? '2px solid #1e3a8a' : '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                background: '#ffffff',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: activo ? '0 0 0 2px rgba(30,58,138,0.15)' : 'none'
                                            }}
                                            title={l.nombre || `Logo ${i + 1}`}
                                        >
                                            <img src={l.url} alt={`Logo ${i + 1}`} style={{ maxHeight: '32px', maxWidth: '48px', objectFit: 'contain' }} />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="editor-grid-3">
                        <div className="editor-field-group">
                            <label>Nombre de la Empresa Emisora *</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.emisor?.nombre || ''}
                                onChange={(e) => handleUpdate('emisor.nombre', e.target.value)}
                                placeholder="Ej. Domus Habita"
                            />
                            <span style={{ fontSize: '10.5px', color: '#94a3b8' }}>
                                * Este nombre se sincroniza automáticamente con el pie de página oficial.
                            </span>
                        </div>
                        <div className="editor-field-group">
                            <label>Slogan o Subtítulo Comercial</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.emisor?.slogan || ''}
                                onChange={(e) => handleUpdate('emisor.slogan', e.target.value)}
                                placeholder="Ej. Soluciones Residenciales & Control de Accesos"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Categoría Oficial del Documento</label>
                            <select
                                className="editor-input"
                                style={{ fontWeight: 700, color: '#1e3a8a', background: '#f8fafc' }}
                                value={tipoActual}
                                onChange={(e) => handleCambiarCategoria(e.target.value)}
                            >
                                <option value="presupuesto">📑 Presupuesto Formal</option>
                                <option value="remision">📋 Remisión de Cobro</option>
                                <option value="pago">🧾 Nota de Pago</option>
                            </select>
                        </div>
                        <div className="editor-field-group">
                            <label>Título Visible en Encabezado</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.tituloDocumento || ''}
                                onChange={(e) => handleUpdate('tituloDocumento', e.target.value)}
                                placeholder="Ej. PRESUPUESTO FORMAL, REMISIÓN DE COBRO, NOTA DE PAGO"
                            />
                        </div>
                    </div>

                    <div className="editor-grid-3">
                        <div className="editor-field-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label>Folio Oficial del Documento</label>
                                <button
                                    type="button"
                                    onClick={handleRegenerarFolioInteligente}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#1e3a8a',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        padding: 0
                                    }}
                                    title="Regenerar automáticamente el folio según la serie, año y cliente"
                                >
                                    🪄 Actualizar Folio
                                </button>
                            </div>
                            <input 
                                type="text"
                                className="editor-input"
                                style={{ fontWeight: 700, color: '#1e3a8a', letterSpacing: '0.4px' }}
                                value={documento.folio || ''}
                                onChange={(e) => handleUpdate('folio', e.target.value)}
                            />
                            <span style={{ fontSize: '10.5px', color: '#64748b' }}>
                                * Serie: {estructuraActiva.nombreLabel} ([Prefijo]-[Año]-[Siglas]-[No.])
                            </span>
                        </div>
                        <div className="editor-field-group">
                            <label>Fecha de Emisión / Expedición</label>
                            <input 
                                type="date"
                                className="editor-input"
                                value={documento.fechaEmision || ''}
                                onChange={(e) => handleUpdate('fechaEmision', e.target.value)}
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>{estructuraActiva.campoFechaLabel}</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={
                                    tipoActual === 'pago' 
                                        ? (documento.fechaPago || '') 
                                        : tipoActual === 'remision' 
                                            ? (documento.fechaVencimiento || '') 
                                            : (documento.fechaVigencia || '')
                                }
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (tipoActual === 'pago') {
                                        handleUpdate('fechaPago', val);
                                        handleUpdate('fechaVigencia', '');
                                        handleUpdate('fechaVencimiento', '');
                                    } else if (tipoActual === 'remision') {
                                        handleUpdate('fechaVencimiento', val);
                                        handleUpdate('fechaVigencia', '');
                                    } else {
                                        handleUpdate('fechaVigencia', val);
                                        handleUpdate('fechaVencimiento', '');
                                    }
                                }}
                                placeholder={estructuraActiva.campoFechaPlaceholder}
                            />
                            <span style={{ fontSize: '10.5px', color: '#64748b' }}>
                                {tipoActual === 'pago' 
                                    ? '* Fecha formal en que se liquidó la totalidad del servicio.' 
                                    : tipoActual === 'remision' 
                                        ? '* Plazo o fecha límite para recibir el pago.' 
                                        : '* Periodo de vigencia garantizada para esta cotización.'}
                            </span>
                        </div>
                    </div>

                    <div className="editor-grid-3">
                        <div className="editor-field-group">
                            <label>RFC Emisor</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.emisor?.rfc || ''}
                                onChange={(e) => handleUpdate('emisor.rfc', e.target.value)}
                                placeholder="XAXX010101000"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Dirección Emisor</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.emisor?.direccion || ''}
                                onChange={(e) => handleUpdate('emisor.direccion', e.target.value)}
                                placeholder="Tablajeros la Calera S/N, Col. Santa Fe"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Teléfono y Correo</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.emisor?.contactoInfo || ''}
                                onChange={(e) => handleUpdate('emisor.contactoInfo', e.target.value)}
                                placeholder="414-226-3804 · domushabitat2025@gmail.com"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* SECCIÓN 2: DATOS DEL CLIENTE / RECEPTOR SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaUserCheck style={{ color: '#0284c7' }} /> 2. {estructuraActiva.seccionReceptorTitulo}
                    </h3>
                    <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="cliente" />
                </div>
                <div className="editor-section-body">
                    <div className="editor-grid-3">
                        <div className="editor-field-group">
                            <label>Nombre del Cliente o Razón Social *</label>
                            <input 
                                type="text"
                                className="editor-input"
                                style={{ fontWeight: 600 }}
                                value={documento.cliente?.nombre || ''}
                                onChange={(e) => {
                                    const nuevoNombre = e.target.value;
                                    handleUpdate('cliente.nombre', nuevoNombre);
                                    if (documento.folio && (documento.folio.includes('-GEN-') || documento.folio.includes('-AVR-') || documento.folio.includes('-FIO-') || documento.folio.includes('-CSF4-'))) {
                                        const folioConSiglas = actualizarSiglasEnFolio(documento.folio, nuevoNombre, tipoActual, configuracion, documento.fechaEmision);
                                        handleUpdate('folio', folioConSiglas);
                                    }
                                }}
                                placeholder="Fraccionamiento Azua, Villa Real"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Atención a (Contacto Principal / Administrador)</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.cliente?.contacto || ''}
                                onChange={(e) => handleUpdate('cliente.contacto', e.target.value)}
                                placeholder="C. Ricardo Cruz (Administrador)"
                            />
                            <span style={{ fontSize: '10.5px', color: '#94a3b8' }}>
                                * Se sincroniza automáticamente con la firma de conformidad del cliente.
                            </span>
                        </div>
                        <div className="editor-field-group">
                            <label>RFC Cliente</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.cliente?.rfc || ''}
                                onChange={(e) => handleUpdate('cliente.rfc', e.target.value)}
                                placeholder="FAV-180420-9B2"
                            />
                        </div>
                    </div>

                    <div className="editor-grid-3">
                        <div className="editor-field-group">
                            <label>Dirección del Condominio / Proyecto</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.cliente?.direccion || ''}
                                onChange={(e) => handleUpdate('cliente.direccion', e.target.value)}
                                placeholder="Av. Villa Real S/N, San Pedro Ahuacatlán"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Municipio y Estado</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.cliente?.municipioEstado || ''}
                                onChange={(e) => handleUpdate('cliente.municipioEstado', e.target.value)}
                                placeholder="San Juan del Río, Qro., C.P. 76810"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Padrón de Residencias / Unidades</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.cliente?.padronResidencial || ''}
                                onChange={(e) => handleUpdate('cliente.padronResidencial', e.target.value)}
                                placeholder="110 Domicilios (Viviendas habitacionales)"
                            />
                        </div>
                    </div>

                    <div className="editor-grid-2">
                        <div className="editor-field-group">
                            <label>Teléfono de Contacto</label>
                            <input 
                                type="text"
                                className="editor-input"
                                value={documento.cliente?.telefono || ''}
                                onChange={(e) => handleUpdate('cliente.telefono', e.target.value)}
                                placeholder="442-190-8451"
                            />
                        </div>
                        <div className="editor-field-group">
                            <label>Correo Electrónico para Envío</label>
                            <input 
                                type="email"
                                className="editor-input"
                                value={documento.cliente?.email || ''}
                                onChange={(e) => handleUpdate('cliente.email', e.target.value)}
                                placeholder="administracion@azuavillareal.mx"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* SECCIÓN 3: CONDICIONES SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaFileContract style={{ color: '#d97706' }} /> 3. {estructuraActiva.seccionCondicionesTitulo}
                    </h3>
                    <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="condiciones" />
                </div>
                <div className="editor-section-body">
                    {documento.tipoDocumento === 'pago' ? (
                        /* NOTA DE PAGO */
                        <>
                            <div className="editor-grid-2">
                                <div className="editor-field-group">
                                    <label>Método de Pago Recibido *</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.metodoPago || documento.condiciones?.terminosPago || ''}
                                        onChange={(e) => {
                                            handleUpdate('metodoPago', e.target.value);
                                            handleUpdate('condiciones.terminosPago', e.target.value);
                                        }}
                                        placeholder="Ej. Transferencia electrónica, Efectivo"
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <label>Referencia Bancaria / Transacción</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.referenciaBancaria || ''}
                                        onChange={(e) => handleUpdate('referenciaBancaria', e.target.value)}
                                        placeholder="Ej. REF-BBVA-908123"
                                    />
                                </div>
                            </div>
                            <div className="editor-grid-2">
                                <div className="editor-field-group">
                                    <label>Periodo Liquidado</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.periodo || ''}
                                        onChange={(e) => handleUpdate('condiciones.periodo', e.target.value)}
                                        placeholder="Vigencia Anual: 12 meses continuos de servicio"
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <label>Constancia / Saldo Insoluto</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.garantia || ''}
                                        onChange={(e) => handleUpdate('condiciones.garantia', e.target.value)}
                                        placeholder="$0.00 Adeudo Pendiente (Saldado al 100%)"
                                    />
                                </div>
                            </div>
                        </>
                    ) : documento.tipoDocumento === 'remision' ? (
                        /* REMISIÓN DE COBRO */
                        <>
                            <div className="editor-grid-2">
                                <div className="editor-field-group">
                                    <label>Periodo de Servicio Devengado</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.periodo || ''}
                                        onChange={(e) => handleUpdate('condiciones.periodo', e.target.value)}
                                        placeholder="Periodo de Evaluación Demo Concluido"
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <label>Forma de Pago / Liquidación Solicitada</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.terminosPago || ''}
                                        onChange={(e) => handleUpdate('condiciones.terminosPago', e.target.value)}
                                        placeholder="Transferencia bancaria o efectivo antes de la fecha límite"
                                    />
                                </div>
                            </div>
                            <div className="editor-grid-2">
                                <div className="editor-field-group">
                                    <label>Fecha Límite / Plazo de Liquidación</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.tiempoEntrega || ''}
                                        onChange={(e) => handleUpdate('condiciones.tiempoEntrega', e.target.value)}
                                        placeholder="Inmediata a la recepción del presente documento"
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <label>Estatus del Saldo</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.garantia || ''}
                                        onChange={(e) => handleUpdate('condiciones.garantia', e.target.value)}
                                        placeholder="Estatus: Pendiente de Liquidación"
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        /* PRESUPUESTO FORMAL: 5 CAMPOS OFICIALES */
                        <>
                            <div className="editor-grid-2">
                                <div className="editor-field-group">
                                    <label>Forma de Pago *</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.formaPago ?? (documento.condiciones?.terminosPago || '')}
                                        onChange={(e) => {
                                            handleUpdate('condiciones.formaPago', e.target.value);
                                            handleUpdate('condiciones.terminosPago', e.target.value);
                                        }}
                                        placeholder="Transferencia electrónica, depósito bancario o efectivo"
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
                                            <span>Inversión Inicial</span>
                                            <span className="badge-inversion-auto" title="Campo inteligente sincronizado automáticamente con el total del presupuesto">
                                                ⚡ Inteligente
                                            </span>
                                        </label>
                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                                            Base: <strong style={{ color: '#0f172a' }}>{formatearMoneda(totalFinal)} MXN</strong> {documento.aplicaIva ? '(Con IVA 16%)' : '(Sin IVA)'}
                                        </span>
                                    </div>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.inversionInicial || ''}
                                        onChange={(e) => handleUpdate('condiciones.inversionInicial', e.target.value)}
                                        placeholder="60% anticipo al autorizar ($1,168.20) · 40% al concluir ($778.80)"
                                    />
                                    {/* Presets rápidos y botón de recálculo inteligente */}
                                    <div className="inversion-presets-wrap">
                                        <button
                                            type="button"
                                            className="inversion-preset-btn"
                                            onClick={() => handleUpdate('condiciones.inversionInicial', generarTextoInversionInicial(totalFinal, 60, 'anticipo al autorizar', 'al concluir'))}
                                            title="Calcular esquema estándar 60% anticipo al autorizar y 40% al concluir"
                                        >
                                            ⚡ 60% / 40% (Recomendado)
                                        </button>
                                        <button
                                            type="button"
                                            className="inversion-preset-btn"
                                            onClick={() => handleUpdate('condiciones.inversionInicial', generarTextoInversionInicial(totalFinal, 50, 'anticipo al autorizar', 'al finiquitar'))}
                                            title="Calcular 50% anticipo y 50% al finiquitar"
                                        >
                                            50% / 50%
                                        </button>
                                        <button
                                            type="button"
                                            className="inversion-preset-btn"
                                            onClick={() => handleUpdate('condiciones.inversionInicial', generarTextoInversionInicial(totalFinal, 100, 'al autorizar', ''))}
                                            title="Calcular liquidación total del 100% de contado"
                                        >
                                            100% Contado
                                        </button>
                                        <button
                                            type="button"
                                            className="inversion-preset-btn recalcular"
                                            onClick={() => handleUpdate('condiciones.inversionInicial', sincronizarTextoInversionInicial(documento.condiciones?.inversionInicial, totalFinal))}
                                            title="Recalcular montos matemáticos con el total actual del presupuesto"
                                        >
                                            🔄 Recalcular ahora
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="editor-grid-2">
                                <div className="editor-field-group">
                                    <label>Plazo de Instalación / Servicio *</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.tiempoEntrega || documento.condiciones?.plazoInstalacion || ''}
                                        onChange={(e) => {
                                            handleUpdate('condiciones.tiempoEntrega', e.target.value);
                                            handleUpdate('condiciones.plazoInstalacion', e.target.value);
                                        }}
                                        placeholder="4 días hábiles de ejecución teniendo material en sitio"
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <label>Renta de Sistemas</label>
                                    <input 
                                        type="text"
                                        className="editor-input"
                                        value={documento.condiciones?.rentaSistemas || ''}
                                        onChange={(e) => handleUpdate('condiciones.rentaSistemas', e.target.value)}
                                        placeholder="$3,400.00 M.N. / mes (Plataforma + Servidor + Respaldo UPS 2.5h)"
                                    />
                                </div>
                            </div>

                            <div className="editor-field-group">
                                <label>Beneficio Exclusivo / Garantías *</label>
                                <input 
                                    type="text"
                                    className="editor-input"
                                    value={documento.condiciones?.garantia || ''}
                                    onChange={(e) => handleUpdate('condiciones.garantia', e.target.value)}
                                    placeholder="12 meses en hardware y soporte de plataforma continuo / 1 mes en mano de obra"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* SECCIÓN 4: DESGLOSE DE PARTIDAS SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaListOl style={{ color: '#16a34a' }} /> 4. {estructuraActiva.seccionPartidasTitulo} ({conceptosTotal})
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="partidas" />
                        <div className="partidas-mode-toggle">
                            <button 
                                type="button" 
                                className={`btn-mode-toggle ${modoVistaPartidas === 'activa' ? 'active' : ''}`}
                                onClick={() => setModoVistaPartidas('activa')}
                                title="Ver y editar una sola partida a la vez (evita scrolls largos)"
                            >
                                📝 1 Partida a la vez
                            </button>
                            <button 
                                type="button" 
                                className={`btn-mode-toggle ${modoVistaPartidas === 'todas' ? 'active' : ''}`}
                                onClick={() => setModoVistaPartidas('todas')}
                                title="Ver todas las partidas apiladas"
                            >
                                📜 Ver todas ({conceptosTotal})
                            </button>
                        </div>
                    </div>
                </div>

                <div className="editor-section-body">
                    {/* Barra de Selección Inteligente de Partida (Título con 100% de espacio) */}
                    {conceptosTotal > 0 && (
                        <div className="partidas-smart-toolbar">
                            <div className="partidas-selector-group" style={{ flex: 1 }}>
                                <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                                    Seleccionar Partida:
                                </label>
                                <select 
                                    className="partida-select-dropdown"
                                    value={partidaValidaIndex}
                                    onChange={(e) => setPartidaActivaIndex(Number(e.target.value))}
                                    style={{ flex: 1, width: '100%' }}
                                >
                                    {(documento.conceptos || []).map((p, idx) => (
                                        <option key={idx} value={idx}>
                                            Partida #{idx + 1}: {(p.descripcion || 'Sin título').substring(0, 75)}... (${(Number(p.precioUnitario) || 0).toLocaleString('es-MX')} MXN)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button 
                                type="button" 
                                className="btn-add-partida-zen"
                                onClick={handleAgregarPartida}
                                style={{ padding: '7px 16px', fontSize: '12px', whiteSpace: 'nowrap' }}
                                title="Agregar una nueva partida en blanco"
                            >
                                <FaPlus /> Nueva
                            </button>
                        </div>
                    )}

                    {/* Fila de Contador / Navegación de Partidas con Herramientas de Tipografía a la derecha */}
                    {conceptosTotal > 0 && (
                        <div 
                            className="partidas-nav-and-typo-row"
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between', 
                                gap: '10px', 
                                flexWrap: 'wrap', 
                                marginBottom: '8px' 
                            }}
                        >
                            {/* Pestañas numéricas / Contador de partidas */}
                            <div className="partidas-nav-pills" style={{ display: 'flex', alignItems: 'center', gap: '4px', overflowX: 'auto', flex: 1, minWidth: '200px' }}>
                                {(documento.conceptos || []).map((p, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        className={`partida-tab-pill ${idx === partidaValidaIndex ? 'active' : ''}`}
                                        onClick={() => setPartidaActivaIndex(idx)}
                                        title={`Ver y editar Partida #${idx + 1}`}
                                    >
                                        #{idx + 1}: {(p.descripcion || 'Partida').substring(0, 18)}...
                                    </button>
                                ))}
                            </div>

                            {/* Sección de Tipografía a la altura del contador de partidas (al final de la última partida) */}
                            <div 
                                className="partida-typo-toolbar-zen"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '7px',
                                    padding: '3px 6px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                    marginLeft: 'auto'
                                }}
                            >
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', marginRight: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <FaFont style={{ fontSize: '11px', color: '#16a34a' }} /> Tipografía:
                                </span>
                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => aplicarFormatoSeleccion('bold')}
                                    title={formatosActivos.bold ? "Negrita ACTIVA: Clic para remover formato" : "Aplicar Negrita al texto seleccionado (**texto**)"}
                                    style={{
                                        background: formatosActivos.bold ? '#dcfce7' : '#f8fafc',
                                        border: formatosActivos.bold ? '1.5px solid #16a34a' : '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        color: formatosActivos.bold ? '#15803d' : '#0f172a',
                                        boxShadow: formatosActivos.bold ? '0 1px 3px rgba(22, 163, 74, 0.25)' : 'none',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <FaBold />
                                </button>
                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => aplicarFormatoSeleccion('italic')}
                                    title={formatosActivos.italic ? "Cursiva ACTIVA: Clic para remover formato" : "Aplicar Cursiva al texto seleccionado (*texto*)"}
                                    style={{
                                        background: formatosActivos.italic ? '#dcfce7' : '#f8fafc',
                                        border: formatosActivos.italic ? '1.5px solid #16a34a' : '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: formatosActivos.italic ? '#15803d' : '#0f172a',
                                        boxShadow: formatosActivos.italic ? '0 1px 3px rgba(22, 163, 74, 0.25)' : 'none',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <FaItalic />
                                </button>
                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => aplicarFormatoSeleccion('underline')}
                                    title={formatosActivos.underline ? "Subrayado ACTIVO: Clic para remover formato" : "Aplicar Subrayado al texto seleccionado (<u>texto</u>)"}
                                    style={{
                                        background: formatosActivos.underline ? '#dcfce7' : '#f8fafc',
                                        border: formatosActivos.underline ? '1.5px solid #16a34a' : '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: formatosActivos.underline ? '#15803d' : '#0f172a',
                                        boxShadow: formatosActivos.underline ? '0 1px 3px rgba(22, 163, 74, 0.25)' : 'none',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <FaUnderline />
                                </button>

                                <span style={{ width: '1px', height: '14px', background: '#cbd5e1', margin: '0 3px' }} />

                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleTipografiaPartida('textAlign', 'left')}
                                    title="Alinear a la izquierda"
                                    style={{
                                        background: (estilosPartidaActiva.textAlign === 'left' || !estilosPartidaActiva.textAlign) ? '#e2e8f0' : '#f8fafc',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: '#334155'
                                    }}
                                >
                                    <FaAlignLeft />
                                </button>
                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleTipografiaPartida('textAlign', 'center')}
                                    title="Centrar texto"
                                    style={{
                                        background: (estilosPartidaActiva.textAlign === 'center') ? '#e2e8f0' : '#f8fafc',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: '#334155'
                                    }}
                                >
                                    <FaAlignCenter />
                                </button>
                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleTipografiaPartida('textAlign', 'right')}
                                    title="Alinear a la derecha"
                                    style={{
                                        background: (estilosPartidaActiva.textAlign === 'right') ? '#e2e8f0' : '#f8fafc',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: '#334155'
                                    }}
                                >
                                    <FaAlignRight />
                                </button>
                                <button 
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleTipografiaPartida('textAlign', 'justify')}
                                    title="Justificar texto"
                                    style={{
                                        background: (estilosPartidaActiva.textAlign === 'justify') ? '#e2e8f0' : '#f8fafc',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        padding: '4px 7px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: '#334155'
                                    }}
                                >
                                    <FaAlignJustify />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Renderizado según modo: 1 Partida a la vez (Recomendado) o Todas */}
                    {conceptosTotal === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                            <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px 0' }}>
                                Aún no has agregado partidas a este documento.
                            </p>
                            <button type="button" className="btn-add-partida-zen" onClick={handleAgregarPartida}>
                                <FaPlus /> Agregar Primera Partida
                            </button>
                        </div>
                    ) : modoVistaPartidas === 'activa' && partidaActiva ? (
                        // MODO 1 PARTIDA A LA VEZ: Ultra cómodo, sin scroll gigantesco
                        <div className="partida-card-item">
                            <div className="partida-header-row">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="partida-number-badge">
                                        Partida #{partidaValidaIndex + 1} de {conceptosTotal}
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                                        Importe acumulado: <strong>${((Number(partidaActiva.cantidad) || 0) * (Number(partidaActiva.precioUnitario) || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong>
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button 
                                        type="button" 
                                        className="btn-remove-partida"
                                        onClick={() => handleDuplicarPartida(partidaValidaIndex)}
                                        title="Duplicar esta partida"
                                        style={{ color: '#0284c7' }}
                                    >
                                        <FaCopy /> Duplicar
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn-remove-partida"
                                        onClick={() => handleEliminarPartida(partidaValidaIndex)}
                                        title="Eliminar esta partida"
                                    >
                                        <FaTrash /> Eliminar
                                    </button>
                                </div>
                            </div>

                            {/* Título de la Partida */}
                            <div className="editor-field-group">
                                <label>Título o Nombre de la Partida / Concepto *</label>
                                <input 
                                    ref={descripcionInputRef}
                                    type="text"
                                    className="editor-input"
                                    style={{ 
                                        textAlign: estilosPartidaActiva.textAlign || 'left',
                                        color: '#0f172a' 
                                    }}
                                    value={partidaActiva.descripcion || ''}
                                    onFocus={(e) => registrarSeleccion('descripcion', e)}
                                    onSelect={(e) => registrarSeleccion('descripcion', e)}
                                    onKeyUp={(e) => registrarSeleccion('descripcion', e)}
                                    onMouseUp={(e) => registrarSeleccion('descripcion', e)}
                                    onChange={(e) => handleUpdateConcepto(partidaValidaIndex, 'descripcion', e.target.value)}
                                    placeholder="Ej. Suministro de Hardware y Dispositivos de Control de Acceso"
                                />
                            </div>

                            {/* Descripción Técnica Inteligente y Adaptativa */}
                            <div className="editor-field-group textarea-smart-wrapper">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label>Descripción Técnica Detallada / Alcance de la Solución</label>
                                    <button 
                                        type="button" 
                                        className="btn-textarea-toggle"
                                        onClick={() => toggleExpandDescripcion(partidaValidaIndex)}
                                        title="Alternar tamaño del recuadro para ahorrar espacio"
                                    >
                                        <FaArrowsAltV /> {expandedDescripciones[partidaValidaIndex] ? 'Reducir a 1 fila' : 'Expandir área de texto'}
                                    </button>
                                </div>
                                <textarea 
                                    ref={subtituloTextareaRef}
                                    rows={expandedDescripciones[partidaValidaIndex] ? 8 : 1}
                                    className={`editor-textarea ${expandedDescripciones[partidaValidaIndex] ? '' : 'textarea-una-linea'}`}
                                    style={{ 
                                        textAlign: estilosPartidaActiva.textAlign || 'left' 
                                    }}
                                    value={partidaActiva.subtitulo || ''}
                                    onFocus={(e) => registrarSeleccion('subtitulo', e)}
                                    onSelect={(e) => registrarSeleccion('subtitulo', e)}
                                    onKeyUp={(e) => registrarSeleccion('subtitulo', e)}
                                    onMouseUp={(e) => registrarSeleccion('subtitulo', e)}
                                    onChange={(e) => handleUpdateConcepto(partidaValidaIndex, 'subtitulo', e.target.value)}
                                    placeholder="Detalla aquí las características técnicas, mano de obra, marcas, especificaciones, etc. Este recuadro es inteligente y evita scrolls largos."
                                />

                                {/* Vista previa interactiva de texto formateado (Negritas, Cursivas, Subrayados) */}
                                {tieneFormatoEnriquecido(partidaActiva.subtitulo) && (
                                    <div 
                                        className="partida-preview-formato-enriquecido animate-fade-in"
                                        style={{
                                            marginTop: '6px',
                                            padding: '8px 12px',
                                            background: '#f8fafc',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            color: '#1e293b',
                                            lineHeight: 1.5
                                        }}
                                    >
                                        <div style={{ fontSize: '10.5px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>👁️ Vista Previa del Formato (Impresión y Lienzo):</span>
                                        </div>
                                        <div 
                                            style={{ textAlign: estilosPartidaActiva.textAlign || 'left', whiteSpace: 'pre-wrap' }}
                                            dangerouslySetInnerHTML={{ __html: formatearTextoEnriquecidoHtml(partidaActiva.subtitulo) }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Fila Numérica */}
                            <div className="editor-grid-4">
                                <div className="editor-field-group">
                                    <label>Cantidad</label>
                                    <input 
                                        type="number"
                                        min="1"
                                        className="editor-input"
                                        value={partidaActiva.cantidad}
                                        onChange={(e) => handleUpdateConcepto(partidaValidaIndex, 'cantidad', e.target.value)}
                                    />
                                </div>
                                <div className="editor-field-group">
                                    <label>Unidad de Medida</label>
                                    <select 
                                        className="editor-input"
                                        value={partidaActiva.unidad || 'Servicio'}
                                        onChange={(e) => handleUpdateConcepto(partidaValidaIndex, 'unidad', e.target.value)}
                                    >
                                        {UNIDADES_LIST.map(u => (
                                            <option key={u} value={u}>{u}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="editor-field-group">
                                    <label>Precio Unitario ($ MXN) *</label>
                                    <div className="editor-currency-wrapper">
                                        <span className="currency-prefix">$</span>
                                        <input 
                                            type="number"
                                            step="0.01"
                                            className="editor-input-currency"
                                            value={partidaActiva.precioUnitario}
                                            onChange={(e) => handleUpdateConcepto(partidaValidaIndex, 'precioUnitario', e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div className="editor-field-group">
                                    <label>Importe Calculado</label>
                                    <div style={{ padding: '9px 12px', background: '#e2e8f0', borderRadius: '6px', fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                                        ${((Number(partidaActiva.cantidad) || 0) * (Number(partidaActiva.precioUnitario) || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // MODO TODAS LAS PARTIDAS APILADAS
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {(documento.conceptos || []).map((partida, idx) => {
                                const cant = Number(partida.cantidad) || 0;
                                const precio = Number(partida.precioUnitario) || 0;
                                const importeTotal = cant * precio;

                                return (
                                    <div key={partida.id || idx} className="partida-card-item">
                                        <div className="partida-header-row">
                                            <span className="partida-number-badge">Partida #{idx + 1}</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button 
                                                    type="button" 
                                                    className="btn-remove-partida"
                                                    onClick={() => handleDuplicarPartida(idx)}
                                                    style={{ color: '#0284c7' }}
                                                    title="Duplicar esta partida"
                                                >
                                                    <FaCopy /> Duplicar
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className="btn-remove-partida"
                                                    onClick={() => handleEliminarPartida(idx)}
                                                    title="Eliminar esta partida"
                                                >
                                                    <FaTrash /> Eliminar
                                                </button>
                                            </div>
                                        </div>

                                        <div className="editor-field-group">
                                            <label>Título o Nombre de la Partida / Servicio *</label>
                                            <input 
                                                type="text"
                                                className="editor-input"
                                                style={{ 
                                                    textAlign: (partida.estilos?.textAlign || estilosPartidaActiva.textAlign || 'left'),
                                                    color: '#0f172a' 
                                                }}
                                                value={partida.descripcion || ''}
                                                onFocus={(e) => registrarSeleccion('descripcion', e)}
                                                onSelect={(e) => registrarSeleccion('descripcion', e)}
                                                onKeyUp={(e) => registrarSeleccion('descripcion', e)}
                                                onMouseUp={(e) => registrarSeleccion('descripcion', e)}
                                                onChange={(e) => handleUpdateConcepto(idx, 'descripcion', e.target.value)}
                                            />
                                        </div>

                                        <div className="editor-field-group textarea-smart-wrapper">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label>Descripción Técnica Detallada</label>
                                                <button 
                                                    type="button" 
                                                    className="btn-textarea-toggle"
                                                    onClick={() => toggleExpandDescripcion(idx)}
                                                >
                                                    <FaArrowsAltV /> {expandedDescripciones[idx] ? 'Reducir a 1 fila' : 'Expandir'}
                                                </button>
                                            </div>
                                            <textarea 
                                                rows={expandedDescripciones[idx] ? 7 : 1}
                                                className={`editor-textarea ${expandedDescripciones[idx] ? '' : 'textarea-una-linea'}`}
                                                style={{ 
                                                    textAlign: (partida.estilos?.textAlign || estilosPartidaActiva.textAlign || 'left')
                                                }}
                                                value={partida.subtitulo || ''}
                                                onFocus={(e) => registrarSeleccion('subtitulo', e)}
                                                onSelect={(e) => registrarSeleccion('subtitulo', e)}
                                                onKeyUp={(e) => registrarSeleccion('subtitulo', e)}
                                                onMouseUp={(e) => registrarSeleccion('subtitulo', e)}
                                                onChange={(e) => handleUpdateConcepto(idx, 'subtitulo', e.target.value)}
                                            />

                                            {tieneFormatoEnriquecido(partida.subtitulo) && (
                                                <div 
                                                    className="partida-preview-formato-enriquecido animate-fade-in"
                                                    style={{
                                                        marginTop: '6px',
                                                        padding: '8px 12px',
                                                        background: '#f8fafc',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        color: '#1e293b',
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    <div style={{ fontSize: '10.5px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.4px' }}>
                                                        👁️ Vista Previa del Formato:
                                                    </div>
                                                    <div 
                                                        style={{ textAlign: (partida.estilos?.textAlign || estilosPartidaActiva.textAlign || 'left'), whiteSpace: 'pre-wrap' }}
                                                        dangerouslySetInnerHTML={{ __html: formatearTextoEnriquecidoHtml(partida.subtitulo) }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="editor-grid-4">
                                            <div className="editor-field-group">
                                                <label>Cantidad</label>
                                                <input 
                                                    type="number"
                                                    min="1"
                                                    className="editor-input"
                                                    value={partida.cantidad}
                                                    onChange={(e) => handleUpdateConcepto(idx, 'cantidad', e.target.value)}
                                                />
                                            </div>
                                            <div className="editor-field-group">
                                                <label>Unidad de Medida</label>
                                                <select 
                                                    className="editor-input"
                                                    value={partida.unidad || 'Servicio'}
                                                    onChange={(e) => handleUpdateConcepto(idx, 'unidad', e.target.value)}
                                                >
                                                    {UNIDADES_LIST.map(u => (
                                                        <option key={u} value={u}>{u}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="editor-field-group">
                                                <label>Precio Unitario ($ MXN) *</label>
                                                <div className="editor-currency-wrapper">
                                                    <span className="currency-prefix">$</span>
                                                    <input 
                                                        type="number"
                                                        step="0.01"
                                                        className="editor-input-currency"
                                                        value={partida.precioUnitario}
                                                        onChange={(e) => handleUpdateConcepto(idx, 'precioUnitario', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="editor-field-group">
                                                <label>Importe Calculado</label>
                                                <div style={{ padding: '9px 12px', background: '#e2e8f0', borderRadius: '6px', fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                                                    ${importeTotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="partidas-actions-bar">
                        <button 
                            type="button" 
                            className="btn-add-partida-zen"
                            onClick={handleAgregarPartida}
                        >
                            <FaPlus /> Agregar Nueva Partida en Blanco
                        </button>
                    </div>
                </div>
            </div>

            {/* SECCIÓN 5: TOTALES SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaCalculator style={{ color: '#0d9488' }} /> 5. {estructuraActiva.seccionTotalesTitulo}
                    </h3>
                    <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="totales" />
                </div>
                <div className="editor-section-body">
                    <div className="editor-grid-2">
                        <div className="editor-field-group">
                            <label>Descuento Especial ($ MXN)</label>
                            <div className="editor-currency-wrapper">
                                <span className="currency-prefix">$</span>
                                <input 
                                    type="number"
                                    step="0.01"
                                    className="editor-input-currency"
                                    value={documento.descuento || 0}
                                    onChange={(e) => handleUpdate('descuento', Number(e.target.value))}
                                />
                            </div>
                        </div>

                        <div className="editor-field-group" style={{ justifyContent: 'center' }}>
                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' }}>
                                <input 
                                    type="checkbox"
                                    checked={documento.aplicaIva || false}
                                    onChange={(e) => handleUpdate('aplicaIva', e.target.checked)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                                    Desglosar I.V.A. (16% para Facturación Fiscal con CFDI)
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="totales-summary-box">
                        <div className="totales-row">
                            <span>Subtotal de Partidas:</span>
                            <strong>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong>
                        </div>
                        {totalDescuento > 0 && (
                            <div className="totales-row" style={{ color: '#16a34a' }}>
                                <span>Descuento / Bonificación Aplicada:</span>
                                <strong>-${totalDescuento.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong>
                            </div>
                        )}
                        {documento.aplicaIva && (
                            <div className="totales-row">
                                <span>IVA (16%):</span>
                                <strong>${montoIva.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong>
                            </div>
                        )}
                        <div className="totales-row total-destacado">
                            <span>TOTAL FINAL DEL DOCUMENTO:</span>
                            <span className="monto-total">${totalFinal.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                        </div>
                    </div>

                    {/* Importe con Letra */}
                    <div className="importe-letras-box">
                        <strong>IMPORTE CON LETRA OFICIAL:</strong> {importeEnLetras}
                    </div>
                </div>
            </div>

            {/* SECCIÓN 6: DATOS BANCARIOS SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaUniversity style={{ color: '#15803d' }} /> 6. {estructuraActiva.seccionBancosTitulo}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="banco" />
                        <button
                            type="button"
                            onClick={handleAgregarCuentaBancaria}
                            style={{
                                background: '#f0fdf4',
                                color: '#15803d',
                                border: '1px solid #86efac',
                                borderRadius: '6px',
                                padding: '4px 10px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                            title="Agregar otra cuenta bancaria al lienzo"
                        >
                            <FaPlus /> Agregar otra Cuenta
                        </button>
                    </div>
                </div>
                <div className="editor-section-body">
                    {/* Barra de Cuentas Configuradas */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px', background: '#f8fafc', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '260px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 700, color: '#1e3a8a', whiteSpace: 'nowrap' }}>
                                📥 Cargar desde Cuentas Configuradas:
                            </label>
                            <select
                                className="editor-input"
                                style={{ fontSize: '12px', padding: '6px 10px', height: '36px', background: '#ffffff', borderColor: '#86efac', fontWeight: 600, color: '#0f172a' }}
                                defaultValue=""
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleCargarBancoConfigurado(e.target.value, 'reemplazar_primera');
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">-- Cargar y Reemplazar Cuenta Principal --</option>
                                {listaBancosConfigurados.map((cb, i) => (
                                    <option key={cb.id || i} value={cb.id || i}>
                                        🏦 {cb.banco || 'Banco'} {cb.cuenta ? `(Cta: ${cb.cuenta})` : cb.clabe ? `(CLABE: ${cb.clabe.slice(-4)})` : ''} - {cb.beneficiario || cb.titular || 'Domus Habita'}
                                    </option>
                                ))}
                            </select>
                            <select
                                className="editor-input"
                                style={{ fontSize: '12px', padding: '6px 10px', height: '36px', background: '#ecfdf5', borderColor: '#10b981', fontWeight: 600, color: '#047857' }}
                                defaultValue=""
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleCargarBancoConfigurado(e.target.value, 'agregar_nueva');
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">-- + Añadir como Cuenta Adicional --</option>
                                {listaBancosConfigurados.map((cb, i) => (
                                    <option key={cb.id || i} value={cb.id || i}>
                                        ➕ Añadir {cb.banco || 'Banco'} {cb.cuenta ? `(Cta: ${cb.cuenta})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Lista Dinámica de Cuentas Bancarias */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {(Array.isArray(documento.cuentasBancarias) && documento.cuentasBancarias.length > 0 ? documento.cuentasBancarias : [{}]).map((cb, idx) => (
                            <div 
                                key={cb.id || idx} 
                                style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '12px 14px',
                                    background: idx === 0 ? '#ffffff' : '#fcfcfd',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: idx === 0 ? '#15803d' : '#0284c7', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FaUniversity /> Cuenta Bancaria #{idx + 1} {idx === 0 ? '(Principal)' : '(Secundaria)'}
                                    </span>
                                    {(documento.cuentasBancarias || []).length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleEliminarCuentaBancaria(idx)}
                                            style={{
                                                background: '#fef2f2',
                                                color: '#ef4444',
                                                border: '1px solid #fecaca',
                                                borderRadius: '4px',
                                                padding: '2px 8px',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                            title="Quitar esta cuenta del documento"
                                        >
                                            <FaTrash /> Eliminar Cuenta
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                                    <div className="editor-field-group">
                                        <label>Banco</label>
                                        <input 
                                            type="text"
                                            className="editor-input"
                                            value={cb?.banco || ''}
                                            onChange={(e) => handleUpdate(`cuentasBancarias.${idx}.banco`, e.target.value)}
                                            placeholder="BBVA Bancomer"
                                        />
                                    </div>
                                    <div className="editor-field-group">
                                        <label>Titular / Beneficiario</label>
                                        <input 
                                            type="text"
                                            className="editor-input"
                                            value={cb?.titular || cb?.beneficiario || ''}
                                            onChange={(e) => handleUpdate(`cuentasBancarias.${idx}.titular`, e.target.value)}
                                            placeholder="Domus Habita Soluciones Residenciales"
                                        />
                                    </div>
                                    <div className="editor-field-group">
                                        <label>CLABE Interbancaria (18 dígitos)</label>
                                        <input 
                                            type="text"
                                            maxLength="18"
                                            className="editor-input"
                                            value={cb?.clabe || ''}
                                            onChange={(e) => handleUpdate(`cuentasBancarias.${idx}.clabe`, e.target.value)}
                                            placeholder="012 680 01548239012 4"
                                        />
                                    </div>
                                    <div className="editor-field-group">
                                        <label>Número de Cuenta</label>
                                        <input 
                                            type="text"
                                            className="editor-input"
                                            value={cb?.cuenta || ''}
                                            onChange={(e) => handleUpdate(`cuentasBancarias.${idx}.cuenta`, e.target.value)}
                                            placeholder="1548239012"
                                        />
                                    </div>
                                    <div className="editor-field-group">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <FaCreditCard style={{ color: '#0284c7' }} /> Número de Tarjeta (16 dígitos)
                                        </label>
                                        <input 
                                            type="text"
                                            maxLength="19"
                                            className="editor-input"
                                            value={cb?.tarjeta || ''}
                                            onChange={(e) => handleUpdate(`cuentasBancarias.${idx}.tarjeta`, e.target.value)}
                                            placeholder="4152 3134 5678 9012"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: '12px', textAlign: 'right' }}>
                        <button
                            type="button"
                            onClick={handleAgregarCuentaBancaria}
                            style={{
                                background: '#f8fafc',
                                color: '#15803d',
                                border: '1px dashed #86efac',
                                borderRadius: '6px',
                                padding: '6px 14px',
                                fontSize: '12px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <FaPlus /> + Agregar otra Cuenta Bancaria al Documento
                        </button>
                    </div>
                </div>
            </div>

            {/* SECCIÓN 7: NOTA CLAVE SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaCommentDots style={{ color: '#0284c7' }} /> 7. {estructuraActiva.seccionNotaClaveTitulo}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="notaClave" />
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
                            <input 
                                type="checkbox"
                                checked={documento.mostrarNotaClave !== false}
                                onChange={(e) => handleUpdate('mostrarNotaClave', e.target.checked)}
                                style={{ cursor: 'pointer' }}
                            />
                            <span>Mostrar en Documento Impreso</span>
                        </label>
                    </div>
                </div>
                <div className="editor-section-body">
                    <div className="editor-grid-2">
                        <div className="editor-field-group">
                            {/* Cabecera nivelada con altura exacta para alineación perfecta con plantillas */}
                            <div style={{ display: 'flex', alignItems: 'center', minHeight: '26px', marginBottom: '4px' }}>
                                <label style={{ margin: 0 }}>Título del Recuadro de Nota Clave</label>
                            </div>
                            <input 
                                type="text"
                                className="editor-input"
                                style={{ fontWeight: 700, color: '#1e3a8a' }}
                                value={documento.tituloNotaClave || 'Nota Clave / Términos del Proyecto:'}
                                onChange={(e) => handleUpdate('tituloNotaClave', e.target.value)}
                                placeholder="Ej. Nota Clave, Condiciones Especiales, Términos de Entrega"
                            />
                        </div>

                        {/* Desplegable de Plantillas con Guardar y Eliminar (Punto 1 y 4: Alineado) */}
                        <div className="editor-field-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '26px', marginBottom: '4px' }}>
                                <label style={{ margin: 0 }}>Plantillas Rápidas Predefinidas (1 Clic)</label>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        type="button"
                                        onClick={handleGuardarPlantillaNotaClave}
                                        style={{ background: '#16a34a', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                        title="Guardar el título y texto actual como plantilla personalizada"
                                    >
                                        <FaSave /> Guardar Plantilla
                                    </button>
                                    {plantillaSeleccionadaId && (
                                        <button
                                            type="button"
                                            onClick={handleEliminarPlantillaNotaClave}
                                            style={{ background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            title="Eliminar la plantilla seleccionada del listado"
                                        >
                                            <FaTrash /> Eliminar
                                        </button>
                                    )}
                                </div>
                            </div>
                            <select
                                className="editor-input"
                                style={{ fontWeight: 600, color: '#0f172a' }}
                                value={plantillaSeleccionadaId}
                                onChange={(e) => handleSeleccionarPlantilla(e.target.value)}
                            >
                                <option value="">-- Seleccionar una plantilla predefinida (1 Clic) --</option>
                                {plantillasNotaClave.map((preset) => (
                                    <option key={preset.id} value={preset.id}>
                                        ⚡ {preset.titulo}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="editor-field-group textarea-smart-wrapper" style={{ marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label>Texto Oficial de la Nota Clave / Observaciones Legales</label>
                            <button 
                                type="button" 
                                className="btn-textarea-toggle"
                                onClick={() => setExpandedNotaClave(prev => !prev)}
                            >
                                <FaArrowsAltV /> {expandedNotaClave ? 'Reducir a 2 filas' : 'Expandir área de texto'}
                            </button>
                        </div>
                        <textarea 
                            rows={expandedNotaClave ? 7 : 2}
                            className={`editor-textarea ${expandedNotaClave ? '' : 'textarea-compacta-nota'}`}
                            value={documento.impactoComunitario || ''}
                            onChange={(e) => handleUpdate('impactoComunitario', e.target.value)}
                            placeholder="Redacta o selecciona una plantilla para las notas de ahorro, esquemas llave en mano, políticas de mora o finiquito demo."
                        />
                    </div>
                </div>
            </div>

            {/* SECCIÓN 8: FIRMAS SEGÚN CATEGORÍA */}
            <div className="editor-section-card">
                <div className="editor-section-header">
                    <h3 className="editor-section-title">
                        <FaSignature style={{ color: '#475569' }} /> 8. {estructuraActiva.seccionFirmasTitulo}
                    </h3>
                    <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="compact" seccionId="firmas" />
                </div>
                <div className="editor-section-body">
                    {/* Selector de firma opcional (Punto 1) */}
                    <div className="firma-toggle-card">
                        <label className="firma-toggle-label">
                            <input 
                                type="checkbox"
                                className="firma-toggle-checkbox"
                                checked={documento.incluirFirmaCliente !== false}
                                onChange={(e) => handleUpdate('incluirFirmaCliente', e.target.checked)}
                            />
                            <span>Incluir firma de aceptación del cliente o proveedor receptor</span>
                        </label>
                        <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                            {documento.incluirFirmaCliente === false 
                                ? '⚠️ Desactivada: Se imprimirá únicamente la firma del emisor centrada.' 
                                : '✅ Activada: Se imprimirán ambas firmas en paralelo (Emisor y Receptor).'}
                        </span>
                    </div>

                    <div className="editor-grid-2">
                        {/* Firma Emisor */}
                        <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '13px', color: '#0f172a' }}>Firma del Emisor (Prestador del Servicio)</h4>
                                {documento.firmas?.emisorFirmaUrl && (
                                    <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>
                                        <FaCheckCircle /> Firma Activa
                                    </span>
                                )}
                            </div>
                            <div className="editor-field-group" style={{ marginBottom: '8px' }}>
                                <label>Nombre del Firmante</label>
                                <input 
                                    type="text"
                                    className="editor-input"
                                    value={documento.firmas?.emisorNombre || ''}
                                    onChange={(e) => handleUpdate('firmas.emisorNombre', e.target.value)}
                                    placeholder="Andrés Jonnatan Wong Feregrino"
                                />
                            </div>
                            <div className="editor-field-group" style={{ marginBottom: '12px' }}>
                                <label>Cargo o Puesto</label>
                                <input 
                                    type="text"
                                    className="editor-input"
                                    value={documento.firmas?.emisorCargo || ''}
                                    onChange={(e) => handleUpdate('firmas.emisorCargo', e.target.value)}
                                    placeholder="Dirección General & Proyectos | Domus Habita"
                                />
                            </div>

                            {/* Gestión visual de la Firma del Emisor */}
                            <div style={{ paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                                    Imagen / Rúbrica Digital del Emisor
                                </label>
                                {documento.firmas?.emisorFirmaUrl ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                        <div style={{ height: '48px', width: '130px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '4px', border: '1px dashed #94a3b8' }}>
                                            <img src={documento.firmas.emisorFirmaUrl} alt="Firma Emisor" style={{ maxHeight: '42px', maxWidth: '120px', objectFit: 'contain' }} />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <label style={{ cursor: 'pointer', fontSize: '11.5px', color: '#1e3a8a', fontWeight: '600', textDecoration: 'underline' }}>
                                                Cambiar imagen de firma
                                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSubirFirmaDirecta} />
                                            </label>
                                            <button 
                                                type="button" 
                                                onClick={() => {
                                                    handleUpdate('firmas.emisorFirmaUrl', '');
                                                    handleUpdate('firmaUrl', '');
                                                }}
                                                style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '11px', textAlign: 'left', cursor: 'pointer', padding: 0 }}
                                            >
                                                Quitar firma
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#eff6ff', border: '1px dashed #3b82f6', borderRadius: '6px', color: '#1e40af', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                                            <FaUpload /> Cargar Firma Digital (PNG / JPG)
                                            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSubirFirmaDirecta} />
                                        </label>
                                        {Array.isArray(configuracion.firmas) && configuracion.firmas.length > 0 && (
                                            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>Usar guardada:</span>
                                                {configuracion.firmas.map((f, i) => (
                                                    <button
                                                        key={f.id || i}
                                                        type="button"
                                                        onClick={() => {
                                                            handleUpdate('firmas.emisorFirmaUrl', f.url);
                                                            handleUpdate('firmaUrl', f.url);
                                                        }}
                                                        style={{ padding: '2px 8px', fontSize: '11px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#1e3a8a', fontWeight: '600' }}
                                                    >
                                                        Firma {i + 1}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Firma Receptor (Cliente / Proveedor) */}
                        <div style={{ 
                            background: documento.incluirFirmaCliente === false ? '#f1f5f9' : '#f8fafc', 
                            padding: '14px', 
                            borderRadius: '8px', 
                            border: '1px solid #e2e8f0',
                            opacity: documento.incluirFirmaCliente === false ? 0.6 : 1,
                            transition: 'opacity 0.2s ease'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '13px', color: '#0f172a' }}>
                                    Firma del Cliente Receptor {documento.incluirFirmaCliente === false && '(Desactivada)'}
                                </h4>
                                {documento.incluirFirmaCliente !== false && (
                                    <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '700', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>
                                        Espacio en Blanco
                                    </span>
                                )}
                            </div>
                            <div className="editor-field-group" style={{ marginBottom: '8px' }}>
                                <label>Nombre del Representante (Sincronizado con "Atención a:")</label>
                                <input 
                                    type="text"
                                    className="editor-input"
                                    value={documento.firmas?.receptorNombre || ''}
                                    onChange={(e) => handleUpdate('firmas.receptorNombre', e.target.value)}
                                    placeholder="C. Ricardo Cruz (Administrador)"
                                    disabled={documento.incluirFirmaCliente === false}
                                />
                            </div>
                            <div className="editor-field-group" style={{ marginBottom: '12px' }}>
                                <label>Cargo de Conformidad</label>
                                <input 
                                    type="text"
                                    className="editor-input"
                                    value={documento.firmas?.receptorCargo || ''}
                                    onChange={(e) => handleUpdate('firmas.receptorCargo', e.target.value)}
                                    placeholder="Aceptación y Visto Bueno del Condominio"
                                    disabled={documento.incluirFirmaCliente === false}
                                />
                            </div>

                            {/* Representación visual del espacio de firma del cliente */}
                            <div style={{ paddingTop: '10px', borderTop: '1px dashed #cbd5e1' }}>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                                    Renglón y Espacio de Conformidad
                                </label>
                                {documento.incluirFirmaCliente !== false ? (
                                    <div style={{ background: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px dashed #94a3b8', textAlign: 'center' }}>
                                        <div style={{ height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>
                                            [ Espacio en blanco reservado para firma autógrafa o sello ]
                                        </div>
                                        <div style={{ height: '1.2px', background: '#64748b', width: '85%', margin: '0 auto 4px auto' }}></div>
                                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#0f172a' }}>
                                            {documento.firmas?.receptorNombre || 'Representante Acreditado'}
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#64748b' }}>
                                            {documento.firmas?.receptorCargo || 'Conformidad y Visto Bueno'}
                                        </div>
                                        <span style={{ fontSize: '10.5px', color: '#15803d', display: 'block', marginTop: '6px', fontWeight: '600' }}>
                                            ✓ Se genera listo para que el cliente firme físicamente con pluma.
                                        </span>
                                    </div>
                                ) : (
                                    <div style={{ background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', color: '#64748b', fontSize: '11px', border: '1px solid #e2e8f0' }}>
                                        ⚠️ Firma del cliente desactivada. En la impresión se emitirá únicamente la firma del emisor centrada en la página.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentSectionsEditor;
